/**
 * Local password session (Core's CORE_AUTH_MODE=local).
 *
 * At boot the app asks GET /api/auth/mode. Only an explicit `{ mode: "local" }` switches the login screen
 * to the username/password form (components/auth/LocalLogin.vue); a fetch failure, a non-2xx or anything
 * else means "cognito" and today's Amplify path runs untouched. The session token (HS256, 12 h, minted by
 * POST /api/auth/local/login) lives in localStorage and is returned by getAuthToken() — Cognito users never
 * have one, so their flow is unchanged.
 */

const TOKEN_KEY = 'bridgeos.local.token';
const USER_KEY = 'bridgeos.local.user';
const ISSUER = 'bridgeos-local';

export type AuthMode = 'local' | 'cognito';

export interface LocalUser {
  username: string;
  attributes: { name?: string };
  groups: string[];
  local: true;
}

const apiBase = () => import.meta.env.VITE_API_BASE_URL || '';

function decodePayload(token: string): Record<string, unknown> | null {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

/** True when the (unverified) token was minted by Core's local login. */
export function isLocalSessionToken(token: string | null | undefined): boolean {
  return !!token && decodePayload(token)?.iss === ISSUER;
}

const MODE_TIMEOUT_MS = 5000;

export async function fetchAuthMode(timeoutMs = MODE_TIMEOUT_MS): Promise<AuthMode> {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  try {
    const resp = await fetch(`${apiBase()}/api/auth/mode`, { headers: { Accept: 'application/json' }, signal: ctrl?.signal });
    if (!resp.ok) return 'cognito';
    const data = await resp.json().catch(() => null);
    return data?.mode === 'local' ? 'local' : 'cognito';
  } catch {
    return 'cognito';
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// Resolved once by createApp() before mount (in parallel with the router), read synchronously by App.vue.
let bootMode: AuthMode | null = null;
export async function resolveBootAuthMode(): Promise<AuthMode> {
  bootMode = await fetchAuthMode();
  return bootMode;
}
export function getBootAuthMode(): AuthMode | null {
  return bootMode;
}

/** The active local session token, or null. Clears an expired session as a side effect. */
export function getLocalToken(): string | null {
  let token: string | null = null;
  try { token = localStorage.getItem(TOKEN_KEY); } catch { return null; }
  if (!token) return null;
  const exp = decodePayload(token)?.exp;
  if (typeof exp !== 'number' || exp * 1000 <= Date.now()) {
    clearLocalSession();
    return null;
  }
  return token;
}

export function getLocalUser(): LocalUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? { ...JSON.parse(raw), local: true } : null;
  } catch {
    return null;
  }
}

export function clearLocalSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch { /* storage unavailable */ }
}

export async function loginLocal(username: string, password: string): Promise<{ ok: true; user: LocalUser } | { ok: false; error: string }> {
  try {
    const resp = await fetch(`${apiBase()}/api/auth/local/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const data = await resp.json().catch(() => null);
    if (!resp.ok || !data?.token || !data?.user) {
      return { ok: false, error: data?.error || (resp.status === 401 ? 'Invalid username or password' : `Sign-in failed (HTTP ${resp.status})`) };
    }
    const user: LocalUser = {
      username: data.user.username,
      attributes: { name: data.user.name },
      groups: Array.isArray(data.user.groups) ? data.user.groups : [],
      local: true,
    };
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    return { ok: true, user };
  } catch {
    return { ok: false, error: 'Cannot reach the server — check your connection and try again' };
  }
}

/** Body code Core's auth layer puts on a 401 for a bad local session (backend services/auth/localAuth). */
export const SESSION_INVALID = 'session_invalid';

/**
 * For the session-expiry watch: true (and the local session is cleared) only when `bearer` is the local
 * session token AND Core's auth layer rejected it (`code: "session_invalid"`). Any other 401 — an upstream or
 * proxied one, e.g. the brain exchange — leaves the session alone.
 */
export async function isLocalSessionRejection(bearer: string, res: Response): Promise<boolean> {
  if (!isLocalSessionToken(bearer)) return false;
  let code: unknown = null;
  try { code = (await res.clone().json())?.code; } catch { return false; }
  if (code !== SESSION_INVALID) return false;
  clearLocalSession();
  return true;
}
